<template>
  <div class="flex flex-col gap-y-[24px] bg-surface-primary p-[16px] rounded-[8px]">
    <div class="text-v-ui-gray-900 text-[20px] font-semibold leading-[120%]">
      Общие сведения
    </div>

    <!-- Form item -->
    <div class="flex flex-col gap-y-[8px]">
      <div class="flex gap-x-[2px] text-heading-secondary title-14-medium-500">
        <RequiredIndicator :filled="!!componentModel.name && isNameValid" />
        Наименование КЗС
      </div>
      <div class="relative">
        <VInput
          v-model="componentModel.name"
          placeholder="Введите название  клинически-значимого события"
          contentWidth
          @blur="validateName"
          :disabled="disabled"
          :error="!isNameValid"
        />
        <div v-if="!isNameValid" class="note-12-reg-400 text-v-ui-root-service-alarm mt-[4px]">Заполните обязательное поле</div>
      </div>
    </div>

    <!-- Form item -->
    <div class="flex flex-col gap-y-[8px]">
      <div class="flex gap-x-[2px] text-heading-secondary title-14-medium-500">
        <RequiredIndicator :filled="!!componentModel.nosologyId?.id" />
        Наименование нозологии
      </div>
      <VSelector
        :value="componentModel.nosologyId"
        :items="preparedNosologyList"
        absolute
        titleKey="title"
        size="m"
        valueKey="id"
        variant="gray"
        placeholder="Введите название нозологии или выберите его из списка"
        with-search
        hideSelectAll
        @input="onNosologyChange"
        :disabled="disabled"
      />
    </div>
    <!-- End form item -->

    <VDescription
      v-if="!componentModel.nosologyId"
      type="ghost"
      deviceType="desktop"
      title="Диагнозы МКБ "
      text="Выберите нозологию, чтобы увидеть диагнозы МБК"
    />

    <div class="flex flex-col gap-y-[8px]" v-else>
      <div class="flex gap-x-[2px] text-heading-secondary title-14-medium-500">
        Диагнозы МКБ
      </div>
      <div class="flex gap-x-[8px] flex-wrap gap-y-[8px]">
        <VAttribute
          v-for="diagnosis in componentModel.nosologyId.diagnoses"
          :key="diagnosis.id"
          :content="diagnosis.mkbCode"
        />
      </div>
    </div>
    <!-- End form item -->

    <!-- Form item -->
    <div class="flex flex-col gap-y-[8px]">
      <div class="flex gap-x-[2px] text-heading-secondary title-14-medium-500">
        <RequiredIndicator :filled="!!componentModel.assignmentTemplateId?.id" />
        Шаблон задачи по результатам срабатывания КЗС
      </div>
      <VSelector
        :value="componentModel.assignmentTemplateId"
        :items="preparedAssignmentTemplateList"
        absolute
        titleKey="title"
        valueKey="id"
        variant="gray"
        size="m"
        placeholder="Введите название шаблона или выберите его из списка"
        with-search
        :disabled="disabled"
        hideSelectAll
        @input="(value) => updateModelField('assignmentTemplateId', value)"
      />
    </div>
    <!-- End form item -->

    <!-- Form item -->
    <div class="flex flex-col gap-y-[8px]">
      <div class="flex gap-x-[2px] text-heading-secondary title-14-medium-500">
        Описание КЗС
      </div>
      <VTextArea
        :value="componentModel.description"
        @input="(value) => updateModelField('description', value)"
        placeholder="Добавьте описание КЗС"
        size="m"
        resizable
        :disabled="disabled"
      />
    </div>
    <!-- End form item -->
  </div>
</template>

<script>
import { VInput, VSelector, VDescription, VAttribute, VTextArea } from '@gostelemed.front/rt-doctis.designsystem';
import RequiredIndicator from '@/components/base/RequiredIndicator.vue';

export default {
  model: {
    prop: 'value',
    event: 'input',
  },
  components: {
    VInput,
    VSelector,
    VDescription,
    VAttribute,
    VTextArea,
    RequiredIndicator,
  },
  name: 'CommonSettingsPart',
  props: {
    value: {
      type: Object,
      default: () => ({
        name: null,
        nosologyId: null,
        assignmentTemplateId: null,
        description: '',
      }),
    },
    nosologyList: {
      type: Array,
      required: true,
    },
    assignmentTemplateList: {
      type: Array,
      required: true,
    },
    fullKzsFormData: {
      type: Object,
      required: false,
      default: () => ({}),
    },
    disabled: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      isNameValid: true,
      isFormDataInitialized: false,
    };
  },
  computed: {
    componentModel() {
      return this.value || {};
    },
    preparedNosologyList() {
      return (this.nosologyList || []).map((nosology) => ({
        ...nosology,
        title: nosology.name,
        id: nosology.id,
      }));
    },
    preparedAssignmentTemplateList() {
      return (this.assignmentTemplateList || []).map((assignmentTemplate) => ({
        ...assignmentTemplate,
        title: assignmentTemplate.name,
        id: assignmentTemplate.id,
      }));
    },
  },
  watch: {
    fullKzsFormData: {
      immediate: true,
      handler() {
        this.parseFullKzsFormData();
      },
    },
    preparedNosologyList() {
      this.parseFullKzsFormData();
    },
    preparedAssignmentTemplateList() {
      this.parseFullKzsFormData();
    },
  },
  methods: {
    updateModelField(field, value) {
      this.$emit('input', {
        ...this.componentModel,
        [field]: value,
      });
    },
    onNosologyChange(value) {
      this.$emit('input', {
        ...this.componentModel,
        nosologyId: value,
      });
    },
    validateName() {
      this.isNameValid = !!this.componentModel?.name;
    },
    parseFullKzsFormData() {
      if (this.isFormDataInitialized) return;
      if (!this.fullKzsFormData || !Object.keys(this.fullKzsFormData).length) return;
      if (!this.preparedNosologyList.length || !this.preparedAssignmentTemplateList.length) return;

      const selectedNosology = this.preparedNosologyList.find(
        (nosology) => nosology.id === this.fullKzsFormData.nosologyId
      ) || null;

      const selectedTemplate = this.preparedAssignmentTemplateList.find(
        (assignmentTemplate) => assignmentTemplate.id === this.fullKzsFormData.assignmentTemplateId
      ) || null;

      this.$emit('input', {
        ...this.componentModel,
        name: this.fullKzsFormData.name || null,
        description: this.fullKzsFormData.description || '',
        nosologyId: selectedNosology,
        assignmentTemplateId: selectedTemplate,
      });

      this.isFormDataInitialized = true;
    },
  },
}
</script>
